<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { PORTAL_OPTIONS } from '../utils/imovelShowConstants';

defineProps({
  portalLoading: {
    type: Boolean,
    default: false,
  },
  configured: {
    type: Boolean,
    default: false,
  },
  connected: {
    type: Boolean,
    default: false,
  },
  connectionLabel: {
    type: String,
    default: '',
  },
  portalLabel: {
    type: String,
    default: '',
  },
  manualRows: {
    type: Array,
    default: () => [],
  },
  blockers: {
    type: Array,
    default: () => [],
  },
  warnings: {
    type: Array,
    default: () => [],
  },
  syncStatusLabel: {
    type: String,
    default: '',
  },
  sync: {
    type: Object,
    default: null,
  },
  lastSyncLabel: {
    type: String,
    default: '',
  },
  successMessage: {
    type: String,
    default: '',
  },
  permissionDenied: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  actionLoading: {
    type: String,
    default: '',
  },
  syncAction: {
    type: String,
    default: 'publish',
  },
  historyRows: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['openConsole', 'authorize', 'sync']);

const selectedPortal = defineModel('selectedPortal', {
  type: String,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="mt-5 border-t border-lais-border-soft pt-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <label
        for="imovel-show-portal-selector"
        class="grid gap-1 text-[12px] font-semibold text-lais-text"
      >
        Portal
        <select
          id="imovel-show-portal-selector"
          v-model="selectedPortal"
          class="h-9 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
        >
          <option
            v-for="option in PORTAL_OPTIONS"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <span
        class="inline-flex min-h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold"
        :class="
          connected
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-lais-bg-soft text-lais-text-muted'
        "
      >
        <span
          v-if="portalLoading"
          class="i-lucide-loader-2 size-3 animate-spin"
          aria-hidden="true"
        />
        {{ connectionLabel }}
      </span>
    </div>

    <section
      class="mt-3 grid gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3"
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="text-[12px] font-semibold text-lais-text">
          Configuração manual redigida
        </p>
        <LaisPrimaryButton
          variant="ghost"
          size="sm"
          icon="i-lucide-settings"
          @click="emit('openConsole')"
        >
          Abrir configuração manual
        </LaisPrimaryButton>
      </div>
      <dl class="grid gap-2 text-[12px] sm:grid-cols-2">
        <div
          v-for="row in manualRows"
          :key="`portal-config-${row.label}`"
          class="rounded-lais-sm bg-white px-3 py-2"
        >
          <dt class="text-lais-text-muted">{{ row.label }}</dt>
          <dd class="mt-0.5 break-words font-semibold text-lais-text">
            {{ row.value }}
          </dd>
        </div>
      </dl>
      <ul
        v-if="blockers.length || warnings.length"
        class="grid gap-1 text-[12px] text-lais-text-body"
      >
        <li
          v-for="blocker in blockers"
          :key="`portal-blocker-${blocker.code || blocker.message}`"
          class="rounded-lais-sm bg-red-50 px-3 py-2 text-red-700"
        >
          {{ blocker.message || blocker.code }}
        </li>
        <li
          v-for="warning in warnings"
          :key="`portal-warning-${warning.code || warning.message}`"
          class="rounded-lais-sm bg-amber-50 px-3 py-2 text-amber-800"
        >
          {{ warning.message || warning.code }}
        </li>
      </ul>
    </section>

    <div class="mt-3 flex flex-col gap-1 text-[13px]">
      <p class="flex items-center justify-between gap-3">
        <span class="text-lais-text-muted">Status do portal</span>
        <span class="font-semibold text-lais-text">{{ syncStatusLabel }}</span>
      </p>
      <p class="flex items-center justify-between gap-3">
        <span class="text-lais-text-muted">Referência</span>
        <span class="min-w-0 break-all text-right font-semibold text-lais-text">
          {{ sync?.externalRef || 'Sem referência' }}
        </span>
      </p>
      <p class="flex items-center justify-between gap-3">
        <span class="text-lais-text-muted">Código remoto</span>
        <span class="min-w-0 break-all text-right font-semibold text-lais-text">
          {{ sync?.remoteId || 'Sem código' }}
        </span>
      </p>
      <p class="flex items-center justify-between gap-3">
        <span class="text-lais-text-muted">Último sync</span>
        <span class="font-semibold text-lais-text">{{ lastSyncLabel }}</span>
      </p>
    </div>

    <p
      v-if="successMessage"
      class="mt-3 rounded-lais-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] font-semibold text-emerald-800"
    >
      {{ successMessage }}
    </p>
    <p
      v-if="permissionDenied"
      class="mt-3 rounded-lais-sm border border-lais-danger/40 bg-lais-danger-50 px-3 py-2 text-[12px] leading-relaxed text-lais-danger"
    >
      {{ errorMessage }}
    </p>
    <p
      v-else-if="errorMessage"
      class="mt-3 rounded-lais-sm bg-red-50 px-3 py-2 text-[12px] leading-relaxed text-red-700"
    >
      {{ errorMessage }}
    </p>

    <div class="mt-4 grid gap-2">
      <LaisPrimaryButton
        variant="ghost"
        size="sm"
        icon="i-lucide-key-round"
        :is-loading="actionLoading === 'authorize'"
        :disabled="!configured || !!actionLoading"
        @click="emit('authorize')"
      >
        Autorizar {{ portalLabel }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        variant="primary"
        size="sm"
        icon="i-lucide-refresh-cw"
        :is-loading="['publish', 'update'].includes(actionLoading)"
        :disabled="!connected || !!actionLoading"
        @click="emit('sync', syncAction)"
      >
        Sincronizar {{ portalLabel }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        variant="ghost"
        size="sm"
        icon="i-lucide-circle-off"
        :is-loading="actionLoading === 'unpublish'"
        :disabled="!connected || !sync || !!actionLoading"
        @click="emit('sync', 'unpublish')"
      >
        Retirar de {{ portalLabel }}
      </LaisPrimaryButton>
    </div>

    <section
      v-if="historyRows.length"
      class="mt-4 border-t border-lais-border-soft pt-3"
    >
      <p class="text-[12px] font-semibold text-lais-text">
        Histórico de portal
      </p>
      <ul class="mt-2 grid gap-2 text-[12px] text-lais-text-body">
        <li
          v-for="entry in historyRows"
          :key="entry.key"
          class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2"
        >
          <div class="flex flex-wrap items-start justify-between gap-2">
            <span class="font-semibold text-lais-text">
              {{ entry.portal }} · {{ entry.status }}
            </span>
            <span class="text-lais-text-muted">
              {{ entry.lastSyncedAt }}
            </span>
          </div>
          <p class="mt-1">
            {{ entry.desiredState }} → {{ entry.observedState }}
            <span v-if="entry.lastAction"> · {{ entry.lastAction }} </span>
            <span v-if="entry.lastAttempt">
              · tentativa {{ entry.lastAttempt }}
            </span>
          </p>
          <p v-if="entry.lastError" class="mt-1 text-red-700">
            {{ entry.lastError }}
          </p>
        </li>
      </ul>
    </section>
  </div>
</template>
